/**
 * Danışan dosyasının gözlem formları bölümü.
 *
 * Formun işi tanı koymak değil: ÖLÇÜMÜN GÖREMEDİĞİNİ göstermek.
 * Cihazdaki performans bir yanı, evde ve sınıfta ne olduğu öbür yanı;
 * ikisi ayrışıyorsa bu başlı başına bir bulgu. Ekran bu yüzden farklı
 * kaynakları (veli / öğretmen / rehber / uygulayıcı) yan yana koyuyor.
 *
 * NORM YOK. Toplam puanın kesme noktası, yüzdeliği ya da "yüksek /
 * düşük" etiketi yok ve olmayacak — ürünün geri kalanıyla aynı kural.
 * Puanlar yalnızca AYNI KAYNAĞIN kendi formları arasında
 * karşılaştırılabilir; ekranda yazdığımız da bu.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  DOLDURAN_ADI,
  OGRETMEN_TURU_ADI,
  gozlemApi,
  ogretmenApi,
  type DosyaOgretmeni,
  type GozlemBekleyen,
  type GozlemDolu,
  type GozlemSatiri,
  type GozlemTanimi,
} from '../api'

const gun = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('tr-TR', { dateStyle: 'medium' }) : '—'

export function DosyaGozlem({
  danisanId,
  yazabilir,
  kisiler,
}: {
  danisanId: string
  yazabilir: boolean
  /** Dosyadaki ilgili kişiler — veli isteklerinde biri seçiliyor */
  kisiler: { id: string; adSoyad: string }[]
}) {
  const [formlar, setFormlar] = useState<GozlemSatiri[]>([])
  const [bekleyenler, setBekleyenler] = useState<GozlemBekleyen[]>([])
  const [tanimlar, setTanimlar] = useState<Record<string, GozlemTanimi>>({})
  const [ogretmenler, setOgretmenler] = useState<DosyaOgretmeni[]>([])
  const [acilan, setAcilan] = useState<GozlemDolu | null>(null)
  const [acilanTanim, setAcilanTanim] = useState<GozlemTanimi | null>(null)
  const [isteAcik, setIsteAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const [l, t, o] = await Promise.all([
        gozlemApi.liste(danisanId),
        gozlemApi.tanimlar(),
        ogretmenApi.danisanin(danisanId),
      ])
      setFormlar(l.formlar)
      setBekleyenler(l.bekleyenler)
      setTanimlar(t.formlar)
      setOgretmenler(o.ogretmenler)
    } catch {
      setHata('Gözlem formları alınamadı.')
    }
  }, [danisanId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (acilan) {
    return (
      <FormDetayi
        form={acilan}
        tanim={acilanTanim}
        onGeri={() => {
          setAcilan(null)
          setAcilanTanim(null)
        }}
      />
    )
  }

  return (
    <div className="kart">
      <h2 style={{ margin: '0 0 4px' }}>Gözlem formları</h2>
      <p className="alt" style={{ marginBottom: 12 }}>
        Veliden ve okuldan gelen gözlem. Cihazdaki ölçüm ne yapabildiğini gösteriyor; bu
        formlar günlük hayatta ne olduğunu. İkisinin ayrışması kendi başına bir bulgu.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {bekleyenler.length > 0 && (
        <>
          <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Bekleyenler</strong>
          <table>
            <tbody>
              {bekleyenler.map((b) => (
                <tr key={b.id}>
                  <td>
                    {tanimlar[b.kod]?.ad ?? b.kod}
                    <span className="alt">
                      {' · '}
                      {b.kisi ?? DOLDURAN_ADI[b.dolduran] ?? b.dolduran}
                      {b.ogretmenTuru ? ` (${OGRETMEN_TURU_ADI[b.ogretmenTuru]})` : ''}
                    </span>
                  </td>
                  <td style={{ color: 'var(--muted)' }}>
                    {gun(b.istendi)} istendi
                    {b.bitis ? ` · ${gun(b.bitis)} son gün` : ''}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {yazabilir && (
                      <button
                        onClick={async () => {
                          await gozlemApi.iptal(b.id)
                          await yukle()
                        }}
                      >
                        İptal
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {formlar.length === 0 ? (
        <p className="alt">Doldurulmuş form yok.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Form</th>
              <th>Dolduran</th>
              <th>Tarih</th>
              <th style={{ textAlign: 'right' }}>Toplam</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {formlar.map((f) => (
              <tr key={f.id}>
                <td>{tanimlar[f.kod]?.ad ?? f.kod}</td>
                <td>
                  {DOLDURAN_ADI[f.dolduran] ?? f.dolduran}
                  {f.dolduranAdi ? <span className="alt"> · {f.dolduranAdi}</span> : null}
                </td>
                <td style={{ color: 'var(--muted)' }}>{gun(f.tarih)}</td>
                <td style={{ textAlign: 'right' }}>{Math.round(Number(f.toplam))}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    onClick={async () => {
                      const r = await gozlemApi.form(f.id)
                      setAcilan(r.form)
                      setAcilanTanim(r.tanim)
                    }}
                  >
                    Aç
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {yazabilir && (
        <div className="dugmeler">
          <button onClick={() => setIsteAcik((x) => !x)}>
            {isteAcik ? 'Vazgeç' : 'Form iste'}
          </button>
        </div>
      )}

      {isteAcik && (
        <FormIste
          danisanId={danisanId}
          kisiler={kisiler}
          ogretmenler={ogretmenler}
          onIstendi={() => {
            setIsteAcik(false)
            void yukle()
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function FormIste({
  danisanId,
  kisiler,
  ogretmenler,
  onIstendi,
}: {
  danisanId: string
  kisiler: { id: string; adSoyad: string }[]
  ogretmenler: DosyaOgretmeni[]
  onIstendi: () => void
}) {
  const [kimden, setKimden] = useState<'parent' | 'teacher'>('parent')
  const [kisiId, setKisiId] = useState(kisiler[0]?.id ?? '')
  const [ogretmenId, setOgretmenId] = useState(ogretmenler[0]?.ogretmenId ?? '')
  const [not, setNot] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const iste = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await gozlemApi.iste(danisanId, {
        dolduran: kimden,
        ...(kimden === 'parent' ? { kod: 'veli_gozlem', kisiId } : { ogretmenId }),
        not: not.trim() || undefined,
      })
      onIstendi()
    } catch (x) {
      setHata(
        x instanceof ApiHatasi && x.kod === 'ogretmen_bu_dosyada_degil'
          ? 'Bu öğretmen bu dosyaya atanmış değil. Önce Okul tarafı bölümünden atayın.'
          : 'İstek oluşturulamadı.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <form
      onSubmit={iste}
      style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}
    >
      {hata && <div className="uyari">{hata}</div>}

      <div className="satir">
        <label>
          Kimden
          <select
            value={kimden}
            onChange={(e) => setKimden(e.target.value as 'parent' | 'teacher')}
          >
            <option value="parent">Veli</option>
            <option value="teacher">Okul</option>
          </select>
        </label>

        {kimden === 'parent' ? (
          <label>
            Hangi veli
            <select value={kisiId} onChange={(e) => setKisiId(e.target.value)} required>
              {kisiler.length === 0 && <option value="">Dosyada ilgili kişi yok</option>}
              {kisiler.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.adSoyad}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Hangi öğretmen
            <select
              value={ogretmenId}
              onChange={(e) => setOgretmenId(e.target.value)}
              required
            >
              {ogretmenler.length === 0 && <option value="">Atanmış öğretmen yok</option>}
              {ogretmenler.map((o) => (
                <option key={o.ogretmenId} value={o.ogretmenId}>
                  {o.adSoyad} — {OGRETMEN_TURU_ADI[o.tur]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <label>
        Not (dolduracak kişi görecek)
        <input
          value={not}
          onChange={(e) => setNot(e.target.value)}
          maxLength={400}
          placeholder="Son iki haftayı düşünerek doldurmasını rica ediyorum."
        />
      </label>

      <p className="alt" style={{ marginTop: -6 }}>
        {kimden === 'parent'
          ? 'Form velinin portal ekranında görünür.'
          : 'Form öğretmenin kendi adresindeki listesinde görünür ve 30 gün açık kalır. Hangi form gönderileceğini öğretmenin türü belirliyor.'}
      </p>

      <button
        className="birincil"
        type="submit"
        disabled={
          bekliyor ||
          (kimden === 'parent' ? !kisiId : !ogretmenId) ||
          (kimden === 'parent' ? kisiler.length === 0 : ogretmenler.length === 0)
        }
      >
        {bekliyor ? 'Gönderiliyor…' : 'Form iste'}
      </button>
    </form>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Doldurulmuş formun maddesi maddesine dökümü.
 *
 * Deneme detayında olduğu gibi: toplam puan tek başına bir şey
 * söylemiyor, hangi maddenin işaretlendiği söylüyor. Ters puanlanan
 * maddeler de işaretli — ham cevapla puanın neden farklı olduğu
 * görünsün.
 */
function FormDetayi({
  form,
  tanim,
  onGeri,
}: {
  form: GozlemDolu
  tanim: GozlemTanimi | null
  onGeri: () => void
}) {
  const enYuksek = (tanim?.secenekler.length ?? 4) - 1

  return (
    <div className="kart">
      <div className="dugmeler" style={{ marginBottom: 10 }}>
        <button onClick={onGeri}>← Gözlem formları</button>
      </div>

      <h2 style={{ margin: '0 0 4px' }}>{tanim?.ad ?? form.kod}</h2>
      <p className="alt">
        {DOLDURAN_ADI[form.dolduran] ?? form.dolduran}
        {form.dolduranAdi ? ` · ${form.dolduranAdi}` : ''} · {gun(form.tarih)} · sürüm{' '}
        {form.surum}
      </p>

      <div className="bilgi">
        Toplam <strong>{Math.round(Number(form.toplam))}</strong> ·{' '}
        {form.altlar.cevaplanan}/{form.altlar.madde} madde cevaplanmış. Bu puanın bir normu,
        kesme noktası ya da yüzdelik karşılığı yok. Anlamı, <strong>aynı kaynağın</strong>{' '}
        daha önce doldurduğu formla karşılaştırıldığında ortaya çıkıyor.
      </div>

      {tanim && (
        <>
          <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Alt başlıklar</strong>
          <table>
            <tbody>
              {Object.entries(form.altlar.altlar).map(([k, v]) => (
                <tr key={k}>
                  <td>{tanim.altBasliklar[k] ?? k}</td>
                  <td style={{ textAlign: 'right' }}>
                    {v.puan} / {v.enCok}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Maddeler</strong>
          <table>
            <thead>
              <tr>
                <th>Madde</th>
                <th>İşaretlenen</th>
                <th style={{ textAlign: 'right' }}>Puan</th>
              </tr>
            </thead>
            <tbody>
              {tanim.maddeler.map((m) => {
                const ham = form.cevaplar[m.anahtar]
                const bos = ham === undefined || ham === null
                return (
                  <tr key={m.anahtar} style={bos ? { opacity: 0.5 } : undefined}>
                    <td>
                      {m.metin}
                      {m.ters && <span className="alt"> · ters puanlanıyor</span>}
                    </td>
                    <td>{bos ? 'Boş' : (tanim.secenekler[ham] ?? ham)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {bos ? '—' : m.ters ? enYuksek - ham : ham}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
